import React, { useEffect, useState, useRef } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Col, Row, Input, Button, Card } from "reactstrap";
import DataTable from "react-data-table-component";
import { Triangle } from "react-loader-spinner";
import { wrap } from "lodash";
// import NumberWithCommas from "../../../pages/Pages/Starter/NumberWithCommas";
import OrderServices from "../../../services/OrderingServices/OrderServices";
import BreadCrumb from "../../../Components/Common/BreadCrumb";
import OrderDetailModal from "../component/OrderDetailModal";

const OrderDetail = () => {
  const { id } = useParams();
  const [orderDetail, setOrderDetail] = useState(null);
  const [resetPaginationToggle, setResetPaginationToggle] = useState(false);
  const [loading, setLoading] = useState(true);
  const [filterText, setFilterText] = useState("");
  const printRef = useRef(null);
  const [selectedData, setSelectedData] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const navigate = useNavigate();

  const toggleModal = () => setModalOpen(!modalOpen);

  const customStyles = {
    headCells: {
      style: {
        fontSize: "0.92rem",
        fontWeight: 610,
      },
    },
    rows: {
      style: {
        fontSize: "0.9rem",
      },
    },
  };

  const fetchOrderDetail = async () => {
    try {
      const response = await OrderServices.getOrderDetail(id);
      setOrderDetail(response);
    } catch (error) {
      console.log("🚀 ~ fetchOrderDetail error:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOrderDetail();
  }, [id]);

  const columns = [
    {
      name: "S.N",
      selector: (row, index) => index + 1,
      sortable: true,
      width: "75px",
    },
    {
      name: "Product Name",
      selector: (row) => (
        <Link to={`/item/detail/${row.productId || ""}`}>
          {row.productName}
        </Link>
      ),
      sortable: true,
      wrap: true,
    },
    {
      name: "Notes",
      selector: (row) => row.remarks || "-",
      sortable: true,
      wrap: true,
    },
    {
      name: "Quantity",
      selector: (row) => row.quantity,
      right: true,
      sortable: true,
    },
    {
      name: "Price",
      selector: (row) => <NumberWithCommas number={row.productPrice} />,
      right: true,
      sortable: true,
    },
    {
      name: "Total",
      selector: (row) => (
        <NumberWithCommas number={row.productPrice * row.quantity} />
      ),
      sortable: true,
      right: true,
    },
  ];

  const filteredData =
    orderDetail?.orderDetails?.filter((item) =>
      item.productName?.toLowerCase().includes(filterText.toLowerCase())
    ) || [];

  const handleFilter = (e) => setFilterText(e.target.value);

  const breadcrumbItems = [
    { title: "Back To List", onClick: () => navigate(-1) },
  ];

  const handlePrint = () => {
    if (!printRef.current) return;

    const printContents = printRef.current.innerHTML;
    const printWindow = window.open("", "", "width=800,height=600");

    const styles = Array.from(
      document.querySelectorAll("link[rel='stylesheet'], style")
    )
      .map((node) => node.outerHTML)
      .join("\n");

    printWindow.document.write(`
      <html>
        <head>
          <title>Invoice</title>
          ${styles}
        </head>
        <body>
          ${printContents}
        </body>
      </html>
    `);

    printWindow.document.close();
    printWindow.focus();
    printWindow.print();
    printWindow.close();
  };

  return (
    <>
      {orderDetail ? (
        <div className="page-content">
          <div className="container-fluid">
            <BreadCrumb
              pageTitle="Order"
              title="Order Detail"
              breadcrumbItems={breadcrumbItems}
            />
          </div>

          {/* Printable Section */}
          <div ref={printRef}>
            {/* ===== Invoice Summary Section ===== */}
            <div className="invoice-section mb-4 px-4 py-3 bg-white rounded shadow-sm border">
              <h5 className="fw-semibold mb-3 d-flex align-items-center gap-2">
                <i className="ri-bill-line"></i> Invoice Details
              </h5>

              <Row>
                {/* Left Column */}
                <Col md={6}>
                  <p className="mb-2 justify-content-between d-flex  align-items-center align-content-center">
                    <strong>Order ID:</strong>{" "}
                    <span className="text-muted">
                      {orderDetail.orderRefrenceId}
                    </span>
                  </p>

                  <p className="mb-2 justify-content-between d-flex  align-items-center align-content-center">
                    <strong>Status:</strong>{" "}
                    <span
                      className={`badge rounded-pill ${
                        orderDetail.orderStatus === "Ongoing"
                          ? "bg-secondary text-white"
                          : orderDetail.orderStatus === "Completed"
                          ? "bg-success"
                          : "bg-danger"
                      }`}
                    >
                      {orderDetail.orderStatus}
                    </span>
                  </p>

                  <p className="mb-2 justify-content-between d-flex align-items-center align-content-center">
                    <strong>Payment:</strong>{" "}
                    <span
                      className={`badge rounded-pill  ${
                        orderDetail.paymentStatus === "Paid"
                          ? "bg-success"
                          : orderDetail.paymentStatus === "Pending"
                          ? "bg-warning"
                          : "bg-danger"
                      }`}
                    >
                      {orderDetail.paymentStatus}
                    </span>
                  </p>
                </Col>

                {/* Right Column */}
                <Col md={6}>
                  <p className="mb-2 d-flex justify-content-between">
                    <strong>Total:</strong>{" "}
                    <span className="fw-semibold">
                      <NumberWithCommas number={orderDetail.grandTotal} />
                    </span>
                  </p>
                  <p className="mb-2 d-flex justify-content-between">
                    <strong>Discount:</strong>{" "}
                    <span className="text-success">
                      -<NumberWithCommas number={orderDetail.discountAmount} />
                    </span>
                  </p>
                  <p className="mb-2 d-flex justify-content-between">
                    <strong>Tax:</strong>{" "}
                    <span className="text-muted">
                      <NumberWithCommas number={orderDetail.taxAmount} />
                    </span>
                  </p>
                </Col>
              </Row>
            </div>
            <Row className="d-print-none mt-3">
              <div className="d-flex pb-2 gap-1 justify-content-between">
                <Col lg={4}>
                  <Input
                    type="text"
                    value={filterText}
                    onChange={handleFilter}
                    placeholder="Search by Product Name"
                  />
                </Col>
                <button
                  type="button"
                  className="btn btn-outline-dark"
                  onClick={handlePrint}
                >
                  <i className="bi bi-printer me-1"></i>
                  Print
                </button>
              </div>
            </Row>
            {/* Product Table */}
            <DataTable
              responsive
              striped
              pagination
              fixedHeader
              highlightOnHover
              pointerOnHover
              persistTableHead
              progressPending={loading}
              customStyles={customStyles}
              paginationResetDefaultPage={resetPaginationToggle}
              progressComponent={
                <div className="my-2 text-center">
                  <Triangle
                    visible={true}
                    height="80"
                    width="80"
                    color="#5B71B9"
                    ariaLabel="triangle-loading"
                  />
                  <h5 className="mt-1">Loading...</h5>
                </div>
              }
              columns={columns}
              data={filteredData}
            />
          </div>

          {/* Controls (Not Printed) */}
        </div>
      ) : (
        <div className="text-center my-5">Loading...</div>
      )}

      <OrderDetailModal
        isOpen={modalOpen}
        toggle={toggleModal}
        orderData={selectedData}
      />
    </>
  );
};

export default OrderDetail;
